import React, { useState, useMemo } from 'react';
import { ClipboardCheck, ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertCircle, HelpCircle, FileText } from 'lucide-react';
import type { EvidenceChecklistWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: EvidenceChecklistWidgetData;
}

const STATUS_CONFIG: Record<string, { icon: React.FC<{ className?: string }>; color: string; label: string }> = {
  PRESENT: { icon: CheckCircle2, color: 'text-green-500 dark:text-green-400', label: 'Present' },
  MISSING: { icon: XCircle, color: 'text-red-500 dark:text-red-400', label: 'Missing' },
  PARTIAL: { icon: AlertCircle, color: 'text-amber-500 dark:text-amber-400', label: 'Partial' },
  UNCLEAR: { icon: HelpCircle, color: 'text-gray-400 dark:text-gray-500', label: 'Unclear' },
};

export const EvidenceChecklistWidget: React.FC<Props> = ({ data }) => {
  const [collapsed, setCollapsed] = useState(false);

  const summary = useMemo(() => {
    const counts = { PRESENT: 0, MISSING: 0, PARTIAL: 0, UNCLEAR: 0 };
    for (const item of data.items) {
      const status = item.status in counts ? item.status as keyof typeof counts : 'UNCLEAR';
      counts[status]++;
    }
    return counts;
  }, [data.items]);

  if (!data.items || data.items.length === 0) return null;

  return (
    <div className="bg-rose-50/30 dark:bg-rose-900/5 border-l-3 border-rose-400 dark:border-rose-600">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-rose-50/50 dark:hover:bg-rose-900/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 flex-shrink-0" />}
        <ClipboardCheck className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          {data.title || 'Evidence Checklist'}
        </span>
        <div className="flex items-center gap-1.5 text-[10px]">
          {summary.PRESENT > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              {summary.PRESENT} present
            </span>
          )}
          {summary.MISSING > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {summary.MISSING} missing
            </span>
          )}
          {summary.PARTIAL > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {summary.PARTIAL} partial
            </span>
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-1">
          {data.items.map((item, i) => {
            const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.UNCLEAR;
            const StatusIcon = config.icon;

            return (
              <div key={i} className="flex items-start gap-2 py-1.5 border-b border-rose-100/50 dark:border-rose-900/10 last:border-0">
                <StatusIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${config.color}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-gray-800 dark:text-gray-200">
                    {item.label}
                  </p>
                  {item.detail && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                      {item.detail}
                    </p>
                  )}
                </div>
                {item.documentName && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-cyan-600 dark:text-cyan-400 whitespace-nowrap flex-shrink-0">
                    <FileText className="h-2.5 w-2.5" />
                    {item.documentName}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
